import React, { useMemo, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useAuthModal } from "../../App";
import { useGameSession, useMidnightCountdown, MAX_ATTEMPTS } from "../../hooks/useGameSession";
import { useNextChallenge } from "../../hooks/useNextChallenge";
import ResultModal from "../../components/ResultModal";
import AchievementPopup from "../../components/AchievementPopup";
import AttemptRow from "../../components/AttemptRow";
import HintTile from "../../components/HintTile";
import Tooltip from "../../components/Tooltip";
import ConfirmDialog from "../../components/ConfirmDialog";
import "../../styles/GuessLocationPage.css";

// ─── Hint reveal thresholds (mirrors SpellPage pattern) ───────────────────────

const HINT_CONTINENT_AT  = 3;
const HINT_LEVEL_AT      = 4;
const HINT_ZOOM_FULL_AT  = 2;   // "full image" after 2 attempts
const HINT_ZOOM_MID_AT   = 1;   // "zoomed in" after 1 attempt
// attempt 0 → very zoomed in (max blur/crop)

// ─── Screenshot panel ─────────────────────────────────────────────────────────

function ScreenshotPanel({ imageUrl, iconKey, attemptsUsed, finished }) {
  const zoomClass = finished
    ? "gl-screenshot-img--full"
    : attemptsUsed === 0
      ? "gl-screenshot-img--close"
      : attemptsUsed === 1
        ? "gl-screenshot-img--mid"
        : "gl-screenshot-img--full";

  const tooltipLines = useMemo(() => {
    if (finished) return [{ icon: "🖼️", text: "Full image revealed", type: "active" }];
    if (attemptsUsed === 0) return [
      { icon: "🔍", text: "Screenshot", type: "title" },
      { text: "After attempt " + HINT_ZOOM_MID_AT + ": zoomed out", type: "muted" },
      { text: "After attempt " + HINT_ZOOM_FULL_AT + ": full image", type: "muted" },
    ];
    if (attemptsUsed < HINT_ZOOM_FULL_AT) {
      const rem = Math.max(0, HINT_ZOOM_FULL_AT - attemptsUsed);
      return [
        { icon: "🔄", text: "Screenshot zoomed out", type: "active" },
        { text: "After attempt " + HINT_ZOOM_FULL_AT + ": full image revealed", type: "muted" },
        { text: rem + " attempt" + (rem === 1 ? "" : "s") + " until full image", type: "countdown" },
      ];
    }
    return [
      { icon: "🖼️", text: "Full image is now visible", type: "active" },
      { text: "All visual hints are now revealed", type: "muted" },
    ];
  }, [attemptsUsed, finished]);

  return (
    <div className="gl-screenshot-wrap">
      <Tooltip lines={tooltipLines}>
        <div className={`gl-screenshot-frame ${finished ? "gl-screenshot-frame--done" : ""}`}>
          {imageUrl ? (
            <img
              key={iconKey}
              src={imageUrl}
              alt="Guess this WoW location"
              className={`gl-screenshot-img ${zoomClass}${attemptsUsed > 2 ? " gl-screenshot-img--no-anim" : ""}`}
              draggable={false}
            />
          ) : (
            <div className="gs-sk gl-screenshot-skeleton" />
          )}
        </div>
      </Tooltip>
    </div>
  );
}

// ─── Hint tooltip builders ────────────────────────────────────────────────────

function useContinentTooltip(hintValue, attemptsUsed) {
  return useMemo(() => {
    if (hintValue) return [{ icon: "✦", text: "Continent: " + hintValue, type: "highlight" }];
    if (attemptsUsed >= HINT_CONTINENT_AT) return [{ icon: "⏳", text: "Revealed — make a guess to refresh", type: "muted" }];
    const rem = Math.max(0, HINT_CONTINENT_AT - attemptsUsed);
    return [
      { icon: "🔒", text: "Continent hint", type: "title" },
      { text: "Available after " + HINT_CONTINENT_AT + " attempts", type: "muted" },
      { text: rem + " attempt" + (rem === 1 ? "" : "s") + " remaining", type: "countdown" },
    ];
  }, [hintValue, attemptsUsed]);
}

function useLevelTooltip(hintValue, attemptsUsed) {
  return useMemo(() => {
    if (hintValue) return [{ icon: "✦", text: "Level range: " + hintValue, type: "highlight" }];
    if (attemptsUsed >= HINT_LEVEL_AT) return [{ icon: "⏳", text: "Revealed — make a guess to refresh", type: "muted" }];
    const rem = Math.max(0, HINT_LEVEL_AT - attemptsUsed);
    return [
      { icon: "🔒", text: "Level Range hint", type: "title" },
      { text: "Available after " + HINT_LEVEL_AT + " attempts", type: "muted" },
      { text: rem + " attempt" + (rem === 1 ? "" : "s") + " remaining", type: "countdown" },
    ];
  }, [hintValue, attemptsUsed]);
}

// ─── Text hint tiles ──────────────────────────────────────────────────────────

function LocationHints({ hints, attemptsUsed, animateHints }) {
  // hints can be either:
  //   - array of strings: ["Continent: Eastern Kingdoms", "Level range: 1-10"]  (from LOAD_STATE)
  //   - object: { continent_hint: "Eastern Kingdoms", level_hint: "1-10" }      (from NEW_GUESS)
  const hintMap = useMemo(() => {
    const map = {};
    if (Array.isArray(hints)) {
      hints.forEach(h => {
        if (h.startsWith("Continent:"))   map.continent   = h.replace("Continent: ", "");
        if (h.startsWith("Level range:")) map.level_range = h.replace("Level range: ", "");
        if (h.startsWith("Expansion:"))   map.level_range = h.replace("Expansion: ", "");
      });
    } else if (hints && typeof hints === "object") {
      // accept any reasonable key names the backend might use
      if (hints.continent_hint)  map.continent   = hints.continent_hint;
      if (hints.continent)       map.continent   = hints.continent;
      if (hints.level_hint)      map.level_range = hints.level_hint;
      if (hints.level_range)     map.level_range = hints.level_range;
      if (hints.expansion_hint)  map.level_range = hints.expansion_hint;
    }
    return map;
  }, [hints]);

  const continentTooltip = useContinentTooltip(hintMap.continent, attemptsUsed);
  const levelTooltip     = useLevelTooltip(hintMap.level_range, attemptsUsed);

  return (
    <div className="gs-hints-row">
      <HintTile
        label="Continent"
        value={hintMap.continent}
        revealed={!!hintMap.continent}
        tooltipLines={continentTooltip}
        animateHints={animateHints}
      />
      <HintTile
        label="Level Range"
        value={hintMap.level_range}
        revealed={!!hintMap.level_range}
        tooltipLines={levelTooltip}
        animateHints={animateHints}
      />
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function GuessLocationPage() {
  const navigate  = useNavigate();
  const { open: openAuth } = useAuthModal();
  const countdown = useMidnightCountdown();

  const s = useGameSession({
    gameName:       "locations",
    itemIdKey:      "location_id",
    correctNameKey: "correct_location_name",
    dailyKey:       "daily_location",
  });

  // ── Next Challenge tracking ──
  const { markCompleted, nextChallenge, allCompleted } = useNextChallenge("locations", s.gameMode);

  // Mark this challenge as completed when game finishes
  useEffect(() => {
    if (s.game.finished) {
      markCompleted();
    }
  }, [s.game.finished, markCompleted]);

  // dailyItem is updated by the hook after every guess/skip/surrender
  const imageUrl = s.dailyItem?.image_url ?? null;

  return (
    <div className="gs-root">
      <Helmet>
        <title>Azerodle | Guess the Location</title>
        <meta name="description" content="Identify a zone in Azeroth from a screenshot. A daily challenge for World of Warcraft players." />
      </Helmet>
      <div className="gs-wrapper">

        {/* ── Guest banner ── */}
        {s.isGuest && (
          <div className="gs-guest-banner">
            <span>Playing as <strong>guest</strong> — rewards and statistics unavailable</span>
            <button className="gs-ghost-btn" onClick={openAuth}>Sign up</button>
          </div>
        )}

        <div className="gs-page-title">
          <h1 className="gs-title">Guess the Location</h1>
        </div>

        {/* ── Main panel ── */}
        {s.initialLoading ? (
          <div className="gs-panel gs-panel--ready">
            <div className="gs-panel-spinner"><div className="gs-spinner" /></div>
          </div>
        ) : (
          <div className="gs-panel gs-panel--ready">
            <div className="gs-panel-content">

              {/* Mode toggle */}
              <div className="gs-mode-row">
                {["classic", "retail"].map(mode => (
                  <button
                    key={mode}
                    className={`gs-mode-btn ${s.gameMode === mode ? "active" : ""}`}
                    onClick={() => s.switchMode(mode)}
                  >
                    {mode === "retail" ? "Retail" : "Classic"}
                  </button>
                ))}
              </div>

              <div className="gs-divider" />
              <div className={["gs-panel-body", s.showBody ? "gs-panel-body--ready" : "", s.fading ? "gs-panel-body--fading" : ""].join(" ")}>
                {s.contentReady && (<>

                  {!s.game.finished && (<>

                    {/* Screenshot with tooltip */}
                    <ScreenshotPanel
                      imageUrl={imageUrl}
                      iconKey={s.iconKey}
                      attemptsUsed={s.attemptsUsed}
                      finished={s.game.finished}
                    />

                    {/* Hint tiles — now using HintTile + Tooltip like SpellPage */}
                    <LocationHints
                      hints={s.game.hints}
                      attemptsUsed={s.attemptsUsed}
                      animateHints={s.game.shouldAnimateHints}
                    />

                    {/* Attempt progress */}
                    <div className="gs-progress-wrap">
                      <div className="gs-progress-meta">
                        <span className="gs-progress-label">Attempts</span>
                        <span className={`gs-progress-count ${s.attemptsUsed >= MAX_ATTEMPTS ? "exhausted" : ""}`}>
                          {s.attemptsUsed} / {MAX_ATTEMPTS}
                        </span>
                      </div>
                      <div className="gs-progress-track">
                        <div
                          className={`gs-progress-fill ${s.game.shouldAnimateProgress ? "animate" : ""}`}
                          style={{ width: s.game.progressWidth + "%" }}
                        />
                      </div>
                    </div>
                  </>)}
                  {/* Finished state */}
                  {s.game.finished ? (
                    <div className={`gs-finished ${s.didWin ? "win" : "lose"}`}>
                      <div className="gs-finished-icon">{s.didWin ? "🏆" : "💀"}</div>
                      <p className="gs-finished-title">
                        {s.didWin ? "Already Solved!" : "Game Over"}
                      </p>
                      <p className="gs-finished-sub">
                        {s.didWin
                          ? <>You guessed <strong>{s.game.correctAnswerName}</strong> in <strong>{s.correctAnswer?.attempt_number}</strong> {s.correctAnswer?.attempt_number === 1 ? "attempt" : "attempts"}</>
                          : <>The location was <strong>{s.game.correctAnswerName || "???"}</strong></>
                        }
                      </p>
                      <div className="gs-finished-countdown">
                        <span className="gs-finished-countdown-label">Next location in</span>
                        <span className="gs-finished-countdown-time">{countdown}</span>
                      </div>

                      {/* Next Challenge button */}
                      {nextChallenge ? (
                        <button
                          className="gs-btn-next-challenge"
                          onClick={() => {
                            if (nextChallenge.game === "locations") {
                              // Same game, just switch mode
                              s.switchMode(nextChallenge.mode);
                            } else {
                              // Navigate to different game page with mode param
                              navigate(nextChallenge.path + "?mode=" + nextChallenge.mode);
                            }
                          }}
                        >
                          <span className="gs-btn-next-challenge-text">Next Challenge</span>
                        </button>
                      ) : allCompleted ? (
                        <div className="gs-all-done">
                          <span className="gs-all-done-text">All daily challenges completed!</span>
                        </div>
                      ) : null}
                    </div>
                  ) : (

                    /* Input section */
                    <div className="gs-input-section">
                      <div className="gs-input-wrap">
                        <input
                          className="gs-input"
                          value={s.userAnswer}
                          onChange={s.handleChange}
                          onKeyDown={s.handleKeyDown}
                          placeholder="Type a location name..."
                          autoComplete="off"
                          disabled={s.submitting}
                        />
                        {s.suggestions.length > 0 && (
                          <div className="gs-suggestions">
                            {s.suggestions.map(loc => (
                              <div
                                key={loc.location_id}
                                className="gs-suggestion-item"
                                onClick={() => s.handleSelect(loc)}
                              >
                                <span>{loc.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        className={`gs-btn-gold ${s.submitting ? "submitting" : ""}`}
                        onClick={s.makeGuess}
                        disabled={!s.selectedItem || s.submitting}
                      >
                        {s.submitting ? "Checking..." : "Submit Answer"}
                      </button>

                      <div className="gs-secondary-actions">
                        <button
                          className="gs-btn-secondary gs-btn-skip"
                          onClick={() => s.setConfirmAction("skip")}
                          disabled={s.submitting}
                        >
                          Skip Attempt
                        </button>
                        <button
                          className="gs-btn-secondary gs-btn-surrender"
                          onClick={() => s.setConfirmAction("surrender")}
                          disabled={s.submitting}
                        >
                          Surrender
                        </button>
                      </div>

                      {s.error && <p className="gs-error">⚠ {s.error}</p>}
                    </div>
                  )}

                  {/* Attempt history */}
                  {!s.game.finished && (<>
                    <div className="gs-divider" />
                    <div className="gs-history">
                      <p className="gs-history-label">Attempt History</p>
                      {Array.from({ length: MAX_ATTEMPTS }, (_, i) => {
                        const attempt = s.game.answers[i];
                        return attempt
                          ? (
                            <AttemptRow
                              key={s.gameMode + "-" + attempt.attempt_number}
                              attempt={attempt}
                              index={i}
                              animate={s.game.animateAttempts && i === s.game.answers.length - 1}
                            />
                          ) : (
                            <div key={i} className="gs-attempt-row gs-attempt-empty">
                              <span className="gs-attempt-num">#{i + 1}</span>
                              <span className="gs-attempt-empty-text">—</span>
                            </div>
                          );
                      })}
                    </div>
                  </>)}

                </>)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Dialogs & modals ── */}
      {s.confirmAction && (
        <ConfirmDialog
          type={s.confirmAction}
          onConfirm={s.handleConfirm}
          onCancel={s.handleCancelConfirm}
        />
      )}

        {s.showModal && (
          <ResultModal
            result={s.lastResult}
            onClose={s.handleResultClose}
            isGuest={s.isGuest}
            onRegister={openAuth}
            itemName={s.correctAnswer?.user_answer || s.game.correctAnswerName}
            itemIcon={s.dailyItem?.icon_url}
            itemLabel="location"
            gameName="locations"
            gameMode={s.gameMode}
            answers={s.game.answers}
            stats={s.stats}
            xpResult={s.isGuest ? null : s.xpResult}
          />
        )}

      {s.showAchievement && s.achievementQueue.length > 0 && (
        <AchievementPopup
          achievement={s.achievementQueue[0]}
          onClose={s.handleAchievementClose}
        />
      )}
    </div>
  );
}